import React, { useRef, useState, useMemo, useCallback } from 'react';
import { withTranslation } from 'react-i18next';

import { useFeatureAvailable } from 'shared-hooks/feature';
import { useCanUser } from 'shared-hooks/permission';

import Toggle from 'components/toggle';
import Popout from 'components/popout';
import Select from 'components/select';

import IconCheckCircled from 'icons/icon-check-circled';
import IconEdit from 'icons/icon-edit';
import IconPlus from 'icons/icon-plus';
import IconInfo from 'icons/icon-info';

import './ProjectModulesConfig.styles.css';

const ModuleOption = ({ id, name, description, selected, onChange, t }) => {
    const handleChange = value => {
        onChange(id, value);
    }

    return (
        <div className="project-modules-config-item">
            <div className="project-modules-config-item-body">
                <h4>{ t('modulesConfig.selectOptionTitle', { module: name }) }</h4>

                <p>{ description }</p>
            </div>

            <Toggle
                size="large"
                checkedText={ t('common:YES') }
                uncheckedText={ t('common:NO') }
                checked={selected}
                onChange={handleChange}
            />
        </div>
    );
}

const ModuleDropdownOption = ({ id, embed, name, icon, selected, onChange, onEditEmbedClick }) => {
    const handleClick = () => {
        if (embed) {
            onEditEmbedClick(id);
        } else {
            onChange(id, !selected);
        }
    }

    return (
        <li className={'pair-item' + (selected ? ' selected' : '')} onClick={handleClick}>
            <span className="key">
                { name }
            </span>

            <span className="value">
                { icon ? icon : <Toggle size="small" checked={selected} /> }
            </span>
        </li>
    )
}

const ProjectModulesConfig = ({ asDropdown, asSelect, selectLabel, disabledModules = [], hideOnChange, embeds, children, onChange, onEditEmbedClick, onCreateEmbedClick, t }) => {
    const popoutCtrl = useRef(null);
    const [popoutVisible, setPopoutVisible] = useState(false);
    const hasFormsFeature = useFeatureAvailable('forms');
    const canSeeUpgradeFeatures = useCanUser('seeUpgradeFeatures');

    const handleClick = () => {
        popoutCtrl.current.show();
    }

    const handlePopoutShow = () => {
        setPopoutVisible(true);
    }

    const handlePopoutHide = () => {
        setPopoutVisible(false);
    }

    const options = useMemo(() => {
        const result = [
            {
                id: 'home',
                name: t('common:Dashboard'),
                description: t('modules.home.description')
            },
            {
                id: 'milestones',
                name: t('milestone:Roadmap'),
                description: t('modules.milestones.description')
            },
            {
                id: 'tasks',
                name: t('task:Tasks'),
                description: t('modules.tasks.description')
            }
        ];

        if (!disabledModules.includes('tasks')) {
            result.push({
                id: 'calendar',
                name: t('date:Calendar'),
                description: t('modules.calendar.description')
            });
        }

        result.push(
            {
                id: 'discussion',
                name: t('chat:Discussions'),
                description: t('modules.discussion.description')
            },
            {
                id: 'documents',
                name: t('doc:Docs'),
                description: t('modules.documents.description')
            },
            {
                id: 'files',
                name: t('file:Files'),
                description: t('modules.files.description')
            }
        );

        if (hasFormsFeature || canSeeUpgradeFeatures) {
            result.push({
                id: 'forms',
                name: t('file:Forms'),
                description: t('modules.forms.description')
            });
        }

        result.push(...(embeds || []).map(({ id, title }) => ({
            id,
            name: title,
            icon: <IconEdit />,
            embed: true
        })));

        return result.map(option => ({
            ...option,
            text: option.name,
            selected: !option.embed && !disabledModules.includes(option.id)
        }));
    }, [t, disabledModules, hasFormsFeature, canSeeUpgradeFeatures, embeds]);

    const toggleModule = useCallback((id, enabled) => {
        if (id === 'tasks' && !enabled && !disabledModules.includes('calendar')) {
            onChange('calendar', false);
        }

        onChange(id, enabled);
    }, [disabledModules, onChange])

    const handleSelect = useCallback(({ id, selected }) => {
        toggleModule(id, !selected)
    }, [toggleModule]);

    const handleChange = (moduleId, enabled) => {
        if (options.length - 1 === disabledModules.length && !enabled) {
            return;
        }

        toggleModule(moduleId, enabled);

        if (hideOnChange && popoutCtrl.current) {
            popoutCtrl.current.hide();
        }
    }

    const handleEditEmbedClick = embedId => {
        onEditEmbedClick(embedId);

        popoutCtrl.current?.hide();
    }

    const handleCreateEmbedClick = () => {
        onCreateEmbedClick();

        popoutCtrl.current?.hide();
    }

    if (asDropdown) {
        const childrenWithProps = React.Children.map(children, child => {
            return React.cloneElement(child, {
                onClick: handleClick,
                className: (child.props.className || '') + (popoutVisible ? ' selected' : '')
            });
        });

        return (
            <React.Fragment>
                { childrenWithProps }

                <Popout
                    className="xxxxsmall no-arrow"
                    xAlign="left"
                    defaultXPosition="right"
                    provideController={popoutCtrl}
                    onShow={handlePopoutShow}
                    onHide={handlePopoutHide}
                >
                    <div className="popout-main">
                        <ul className="popout-checklist widget-box-select-list">
                            {
                                options.map(option => (
                                    <ModuleDropdownOption
                                        key={option.id}
                                        id={option.id}
                                        name={option.name}
                                        icon={option.icon}
                                        embed={option.embed}
                                        selected={option.selected}
                                        onChange={handleChange}
                                        onEditEmbedClick={handleEditEmbedClick}
                                    />
                                ))
                            }

                            {
                                embeds ? (
                                    <li key="create">
                                        <button className="btn btn-success btn-small btn-block" onClick={handleCreateEmbedClick}>
                                            <IconPlus />

                                            { t('embed.createTitle') }
                                        </button>
                                    </li>
                                ) : null
                            }
                        </ul>
                    </div>

                    <div className="popout-foot">
                        <div className="foot-info">
                            <IconInfo />

                            <span className="foot-info-main">
                                { t('modulesConfig.descriptionShort') }
                            </span>
                        </div>
                    </div>
                </Popout>
            </React.Fragment>
        );
    }

    if (asSelect) {
        return (
            <Select
                options={options}
                selectedComponent={<div>{selectLabel}</div>}
                onSelect={handleSelect}
            />
        );
    }


    return (
        <div className="project-modules-config">
            {
                options.map(option => <ModuleOption
                    key={option.id}
                    id={option.id}
                    name={option.name}
                    description={option.description}
                    selected={!disabledModules.includes(option.id)}
                    onChange={toggleModule}
                    t={t}
                />)
            }
        </div>
    )
}

export default withTranslation(['project', 'task', 'chat', 'milestone', 'doc', 'file', 'common', 'date'])(ProjectModulesConfig);
